const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-10-15';

class BackInStockComponent extends HTMLElement {
  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.form = this.querySelector('[data-form]');
    this.input = this.querySelector('[data-input]');
    this.button = this.querySelector('[data-submit]');
    this.label = this.querySelector('[data-label]');
    this.successEl = this.querySelector('[data-success]');
    this.errorEl = this.querySelector('[data-error]');
    this.defaultLabel = this.label?.textContent ?? '';

    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  #initialized = false;

  #handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.input || !this.button) return;

    const email = this.input.value.trim();
    if (!email || !this.input.checkValidity()) {
      this.#showError('Please enter a valid email address.');
      this.input.focus();
      return;
    }

    const companyId = this.dataset.companyId;
    const variantId = this.dataset.variantId;
    if (!companyId || !variantId) {
      this.#showError('This form is not configured correctly. Please contact us.');
      return;
    }

    this.#setBusy(true);
    this.#clearMessages();

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      });

      if (response.status === 202 || response.ok) {
        this.#showSuccess();
        return;
      }

      const errorText = await this.#readError(response);
      this.#showError(errorText);
    } catch (err) {
      this.#showError('Something went wrong. Please try again.');
    } finally {
      this.#setBusy(false);
    }
  };

  async #readError(response) {
    try {
      const body = await response.json();
      const detail = body?.errors?.[0]?.detail;
      if (detail) return detail;
    } catch (_) {
      /* fall through */
    }
    if (response.status === 429) return 'Too many requests. Please try again in a moment.';
    if (response.status >= 500) return 'Klaviyo is temporarily unavailable. Please try again.';
    return 'We couldn\'t add you to the list. Please try again.';
  }

  #setBusy(isBusy) {
    if (!this.button || !this.input) return;
    this.button.disabled = isBusy;
    this.button.setAttribute('aria-busy', String(isBusy));
    this.input.disabled = isBusy;
  }

  #clearMessages() {
    this.successEl?.classList.add('hidden');
    this.errorEl?.classList.add('hidden');
    if (this.errorEl) this.errorEl.textContent = '';
  }

  #showSuccess() {
    this.form?.classList.add('hidden');
    this.successEl?.classList.remove('hidden');
    this.errorEl?.classList.add('hidden');
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('hidden');
    this.successEl?.classList.add('hidden');
  }
}

if (!customElements.get('back-in-stock-component')) {
  customElements.define('back-in-stock-component', BackInStockComponent);
}
